import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, Gift, Send, CheckCircle, Store } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { InnerHero } from '@/components/sections'
import { Prefooter, Footer } from '@/components/layout'
import { LocalizedLink } from '@/components/LocalizedLink'

interface FormState {
  importo: string
  modalita: 'stampa' | 'email' | ''
  destinatario: string
  email: string
  messaggio: string
  dataInvio: string
}

export function EGiftCardPage() {
  const { t } = useTranslation('common')

  const faqs = [
    {
      q: t('eGiftCard.faq1Question'),
      a: t('eGiftCard.faq1Answer'),
    },
    {
      q: t('eGiftCard.faq2Question'),
      a: t('eGiftCard.faq2Answer'),
    },
    {
      q: t('eGiftCard.faq3Question'),
      a: t('eGiftCard.faq3Answer'),
    },
    {
      q: t('eGiftCard.faq4Question'),
      a: t('eGiftCard.faq4Answer'),
    },
  ]

  const features = [
    t('eGiftCard.feature1'),
    t('eGiftCard.feature2'),
    t('eGiftCard.feature3'),
    t('eGiftCard.feature4'),
  ]

  const deliveryOptions = [
    { key: 'stampa' as const, label: t('eGiftCard.optionPrint') },
    { key: 'email' as const, label: t('eGiftCard.optionEmail') },
  ]

  const [form, setForm] = useState<FormState>({
    importo: '',
    modalita: '',
    destinatario: '',
    email: '',
    messaggio: '',
    dataInvio: '',
  })
  const [submitted, setSubmitted] = useState(false)
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((s) => ({ ...s, [key]: value }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.importo.trim() || !form.modalita) return
    setSubmitted(true)
  }

  return (
    <main>
      <InnerHero
        eyebrow={t('eGiftCard.heroEyebrow')}
        title={t('eGiftCard.heroTitle')}
        titleItalic={t('eGiftCard.heroTitleItalic')}
        body={t('eGiftCard.heroBody')}
        breadcrumbs={[
          { label: t('nav.home'), href: '/it' },
          { label: t('eGiftCard.breadcrumb') },
        ]}
      />

      {/* Product description */}
      <section className="bg-cream py-20 md:py-28 overflow-hidden">
        <div className="nashi-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1 }}
            >
              <span className="nashi-eyebrow">{t('eGiftCard.eyebrow')}</span>
              <h2
                className="mt-6 font-display text-bruno"
                style={{
                  fontSize: 'clamp(1.875rem, 3vw, 2.75rem)',
                  lineHeight: 1.05,
                  letterSpacing: '-0.015em',
                }}
              >
                <span className="block">{t('eGiftCard.title1')}</span>
                <span className="block italic font-light text-argan-deep">
                  {t('eGiftCard.title2')}
                </span>
              </h2>
              <p className="mt-6 text-bruno-soft leading-relaxed max-w-md">
                {t('eGiftCard.description')}
              </p>
              <div className="mt-8 space-y-4">
                {features.map((item) => (
                  <div key={item} className="flex items-center gap-3 text-sm text-bruno-soft">
                    <Gift className="h-4 w-4 text-argan-deep" strokeWidth={1.5} />
                    {item}
                  </div>
                ))}
              </div>
              <div className="mt-10">
                <LocalizedLink
                  to="storeLocator"
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-bruno border-b border-bruno pb-1 hover:text-argan-deep hover:border-argan-deep transition-colors"
                >
                  <Store className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {t('eGiftCard.findStore')}
                </LocalizedLink>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src="/images/welcome-box.jpg"
                  alt={t('eGiftCard.imageAlt')}
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Purchase form */}
      <section className="bg-cream-soft py-20 md:py-28">
        <div className="nashi-container max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9 }}
          >
            <span className="nashi-eyebrow">{t('eGiftCard.formEyebrow')}</span>
            <h2
              className="mt-6 font-display text-bruno mb-12"
              style={{
                fontSize: 'clamp(1.875rem, 3vw, 2.5rem)',
                lineHeight: 1.05,
                letterSpacing: '-0.015em',
              }}
            >
              <span className="block">{t('eGiftCard.formTitle1')}</span>
              <span className="block italic font-light text-argan-deep">
                {t('eGiftCard.formTitle2')}
              </span>
            </h2>
          </motion.div>

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              className="bg-white px-8 py-14 md:px-14 md:py-20 text-center border border-bruno/10"
            >
              <CheckCircle
                className="mx-auto h-10 w-10 text-argan-deep mb-6"
                strokeWidth={1.5}
              />
              <span className="font-display italic text-argan-deep text-sm tracking-[0.32em] uppercase">
                {t('eGiftCard.thanksEyebrow')}
              </span>
              <h3
                className="mt-6 font-display text-bruno"
                style={{
                  fontSize: 'clamp(2rem, 3.5vw, 3rem)',
                  lineHeight: 1.05,
                  letterSpacing: '-0.02em',
                }}
              >
                <span className="block">{t('eGiftCard.thanksTitle1')}</span>
                <span className="block italic font-light text-argan-deep">
                  {t('eGiftCard.thanksTitle2')}
                </span>
              </h3>
              <p className="mt-6 max-w-md mx-auto text-bruno-soft leading-relaxed">
                {t('eGiftCard.thanksBody')}
              </p>
            </motion.div>
          ) : (
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.9 }}
              onSubmit={handleSubmit}
              className="space-y-6"
              noValidate
            >
              <div>
                <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                  {t('eGiftCard.labelAmount')} <span className="text-argan-deep">*</span>
                </label>
                <input
                  type="number"
                  min={20}
                  max={4999}
                  required
                  value={form.importo}
                  onChange={(e) => update('importo', e.target.value)}
                  placeholder={t('eGiftCard.placeholderAmount')}
                  className="nashi-input w-full"
                />
                <p className="mt-1.5 text-[11px] text-bruno-muted">
                  {t('eGiftCard.amountHint')}
                </p>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                  {t('eGiftCard.labelDelivery')} <span className="text-argan-deep">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {deliveryOptions.map((opt) => (
                    <label
                      key={opt.key}
                      className={`cursor-pointer border p-4 transition-colors ${
                        form.modalita === opt.key
                          ? 'border-bruno bg-cream-soft'
                          : 'border-bruno/10 bg-white hover:border-bruno/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="modalita"
                        value={opt.key}
                        checked={form.modalita === opt.key}
                        onChange={() => update('modalita', opt.key)}
                        className="sr-only"
                      />
                      <span className="text-sm text-bruno">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {form.modalita === 'email' && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="space-y-6"
                >
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                      {t('eGiftCard.labelRecipientEmail')}
                    </label>
                    <input
                      type="email"
                      value={form.destinatario}
                      onChange={(e) => update('destinatario', e.target.value)}
                      placeholder={t('eGiftCard.placeholderEmail')}
                      className="nashi-input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                      {t('eGiftCard.labelConfirmEmail')}
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => update('email', e.target.value)}
                      placeholder={t('eGiftCard.placeholderEmail')}
                      className="nashi-input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                      {t('eGiftCard.labelMessage')}
                    </label>
                    <textarea
                      rows={4}
                      value={form.messaggio}
                      onChange={(e) => update('messaggio', e.target.value)}
                      placeholder={t('eGiftCard.placeholderMessage')}
                      className="nashi-input w-full resize-y min-h-[100px]"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3">
                      {t('eGiftCard.labelSendDate')}
                    </label>
                    <input
                      type="date"
                      value={form.dataInvio}
                      onChange={(e) => update('dataInvio', e.target.value)}
                      className="nashi-input w-full"
                    />
                  </div>
                </motion.div>
              )}

              <div className="pt-4">
                <button
                  type="submit"
                  className="group inline-flex items-center gap-3 bg-bruno text-cream font-medium uppercase tracking-[0.28em] text-[11px] px-10 py-4 hover:bg-argan-deep transition-colors"
                >
                  {t('eGiftCard.buyNow')}
                  <Send
                    className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-0.5"
                    strokeWidth={1.75}
                  />
                </button>
              </div>

              <p className="text-[11px] text-bruno-muted leading-relaxed">
                {t('eGiftCard.termsHint')}
              </p>
            </motion.form>
          )}
        </div>
      </section>

      {/* Terms */}
      <section className="bg-cream py-20 md:py-28">
        <div className="nashi-container max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9 }}
          >
            <span className="nashi-eyebrow">{t('eGiftCard.termsEyebrow')}</span>
            <h2
              className="mt-6 font-display text-bruno mb-10"
              style={{
                fontSize: 'clamp(1.5rem, 2.4vw, 2rem)',
                lineHeight: 1.15,
                letterSpacing: '-0.01em',
              }}
            >
              {t('eGiftCard.termsTitle')}
            </h2>
          </motion.div>

          <div className="space-y-6 text-sm text-bruno-soft leading-relaxed">
            <p>{t('eGiftCard.termsP1')}</p>
            <p>{t('eGiftCard.termsP2')}</p>
            <p>{t('eGiftCard.termsP3')}</p>
            <p>{t('eGiftCard.termsP4')}</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-cream-soft py-20 md:py-28">
        <div className="nashi-container max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9 }}
          >
            <span className="nashi-eyebrow">{t('eGiftCard.faqEyebrow')}</span>
            <h2
              className="mt-6 font-display text-bruno mb-10"
              style={{
                fontSize: 'clamp(1.5rem, 2.4vw, 2rem)',
                lineHeight: 1.15,
                letterSpacing: '-0.01em',
              }}
            >
              {t('eGiftCard.faqTitle')}
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                className="border border-bruno/10 bg-white"
              >
                <button
                  onClick={() =>
                    setOpenFaq(openFaq === idx ? null : idx)
                  }
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-display text-bruno text-base">
                    {faq.q}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 text-bruno-muted transition-transform duration-300 ${
                      openFaq === idx ? 'rotate-180' : ''
                    }`}
                    strokeWidth={1.5}
                  />
                </button>
                <AnimatePresence>
                  {openFaq === idx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-5 text-sm text-bruno-soft leading-relaxed">
                        {faq.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Prefooter />
      <Footer />
    </main>
  )
}
